import React, { useState } from 'react';
import type { AuthError } from '@supabase/supabase-js';
import { useNavigate } from '../lib/routerCompat';
import type { AppRoutePath } from '../lib/routes';
import { supabase } from '../lib/supabase';
import { db } from '../services/db';
import { isCreatorProfileComplete } from '../lib/creatorProfile';
import type { UserRole } from '../types';
import LazyImage from '../components/LazyImage';

const HOME_FOR_ROLE = (role: UserRole): AppRoutePath => (role === 'creator' ? '/creator' : '/feed');

/**
 * Where an authenticated user lands (PIT-48). Brands go to Discover. Creators
 * go to their dashboard only once they own a finished profile — otherwise into
 * the profile builder, since signing up as a creator would otherwise dead-end
 * on a dashboard with no `creators` row behind it. A partially-built profile
 * resumes rather than starting over.
 */
async function homeForAuthedRole(role: UserRole): Promise<AppRoutePath> {
  if (role !== 'creator') return '/feed';
  try {
    return isCreatorProfileComplete(await db.getMyCreator()) ? '/creator' : '/onboarding';
  } catch {
    // Lookup failed — send them to the builder, which resolves the row
    // idempotently and bounces back out if one already exists.
    return '/onboarding';
  }
}

// The invite-only DB trigger rejects non-approved signups with this marker
const INVITE_ONLY_MESSAGE =
  'PitchVault is invite-only right now. Join the waitlist and we’ll email you when your spot opens.';

// GoTrue masks trigger exceptions: depending on version the client sees
// "Database error saving new user", code "unexpected_failure", or a raw
// JSON.stringify of an empty error body ("{}"). Treat all of those — and any
// other 5xx during signup — as the invite gate.
const friendlyAuthError = (err: AuthError): string => {
  const message = typeof err?.message === 'string' ? err.message : '';
  const unhelpful = !message.trim() || /^(\{\s*\}|\[\s*\])$/.test(message.trim());
  if (
    /signups_closed|database error saving new user|signups not allowed/i.test(message) ||
    err?.code === 'unexpected_failure' ||
    (err?.status ?? 0) >= 500 ||
    unhelpful
  ) {
    return INVITE_ONLY_MESSAGE;
  }
  return message;
};

export default function AuthView(): JSX.Element {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [role, setRole] = useState<UserRole>('brand');
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  const submit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');

    // Demo mode: no backend configured, keep the walkthrough behavior. Creators
    // signing up still get the profile builder so the flow is walkable without
    // Supabase — it just persists nothing.
    if (!supabase) {
      const to = mode === 'signup' && role === 'creator' ? '/onboarding' : HOME_FOR_ROLE(role);
      navigate({ to });
      return;
    }

    if (submitting) return;
    setSubmitting(true);

    try {
      if (mode === 'signup') {
        const { data, error: err } = await supabase.auth.signUp({
          email: email.trim().toLowerCase(),
          password,
          options: { data: { role, display_name: name.trim() } },
        });
        if (err) {
          setError(friendlyAuthError(err));
          return;
        }
        if (!data.session) {
          // Email confirmation is enabled on the Supabase project
          setError('Check your inbox to confirm your email, then sign in.');
          return;
        }
        navigate({ to: await homeForAuthedRole(role) });
      } else {
        const { data, error: err } = await supabase.auth.signInWithPassword({
          email: email.trim().toLowerCase(),
          password,
        });
        if (err) {
          setError(err.message);
          return;
        }
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', data.user.id)
          .single();
        navigate({ to: await homeForAuthedRole(profile?.role ?? role) });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-logo">
          <LazyImage
            src="/logo.png"
            alt="PitchVault"
            className="header-logo"
            width={56}
            height={56}
            eager
          />
          Pitch Vault
        </div>
        <div className="auth-subtitle">A UGC Marketplace</div>
        <div className="auth-tagline">
          {mode === 'signin' ? 'Welcome back.' : 'Join the marketplace.'}
        </div>

        <div className="auth-role-row">
          <button
            className={`auth-role-btn${role === 'brand' ? ' active' : ''}`}
            onClick={() => setRole('brand')}
          >
            Brand
          </button>
          <button
            className={`auth-role-btn${role === 'creator' ? ' active' : ''}`}
            onClick={() => setRole('creator')}
          >
            Creator
          </button>
        </div>

        <form className="auth-form" onSubmit={submit}>
          {mode === 'signup' && (
            <div className="auth-field">
              <label className="form-label">{role === 'brand' ? 'Brand Name' : 'Full Name'}</label>
              <input
                className="form-input"
                placeholder={role === 'brand' ? 'e.g. Apex Nutrition' : 'e.g. Jordan Reeves'}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          )}
          <div className="auth-field">
            <label className="form-label">Email</label>
            <input
              className="form-input"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required={Boolean(supabase)}
            />
          </div>
          <div className="auth-field">
            <label className="form-label">Password</label>
            <input
              className="form-input"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required={Boolean(supabase)}
              minLength={supabase ? 6 : undefined}
            />
          </div>
          {error && (
            <p className="auth-error" role="alert">
              {error}
            </p>
          )}
          <button
            type="submit"
            className="btn-submit-gold"
            style={{ marginTop: 8 }}
            disabled={submitting}
          >
            {submitting ? 'One moment…' : mode === 'signin' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <div className="auth-switch">
          {mode === 'signin' ? (
            <>
              Don&apos;t have an account?{' '}
              <button className="auth-switch-btn" onClick={() => setMode('signup')}>
                Create one
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button className="auth-switch-btn" onClick={() => setMode('signin')}>
                Sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
